import {
  Citation,
  Contributor,
  IntialCitationData,
  FormMode,
  BackButtonTexts,
  PubMainTypeEnum,
  PubSuffixTypeEnum,
  SourceType,
  validateMissingFormFields,
} from '@chegg/writing-forms'

import { FormType } from '@chegg-wt/core/constants/form'
import { evalSourceTypes } from '@chegg-wt/core/utils/searchSources'
import { ValidatorStates } from '@chegg-wt/core/state/citation/constants'
import { CITATION_LIST_NAME_LOWER_SINGLE } from '@chegg-wt/core/constants/citations'
import { BffContributor } from './types'

export const SOURCE_WEBPAGE = 'webpage'
export const WEBSITE_PUBTYPE = {
  main: PubMainTypeEnum.PUB_ONLINE,
  suffix: PubSuffixTypeEnum.PUB_NONE,
}

export const backButtonConfigs = {
  manual: {
    text: 'Back to search',
    route: 'toSourceSearch',
  },
  new: {
    text: 'Back to review',
    route: 'toSourceConfirmation',
  },
  edit: {
    text: `Back to ${CITATION_LIST_NAME_LOWER_SINGLE}`,
    route: 'toBibliography',
  },
} as const

export const getFormMode = (formType: string) => {
  if (formType === 'edit') return FormMode.EDIT
  return FormMode.NEW
}
export const mapContributorBffToCitationFormat = (
  contributor: BffContributor
): Contributor => {
  const { first, middle, last, function: fn } = contributor
  return {
    firstName: first ?? null,
    middleName: middle ?? null,
    lastName: last ?? null,
    function: fn ?? null,
    data: contributor.suffix ?? contributor.data ?? null,
  } as Contributor
}

export const formatAutociteDate = (formPubType?: {
  dayaccessed?: string | null
  day?: string | null
}) => {
  const formPubTypeData = { ...formPubType }
  if (formPubTypeData.day ?? false) {
    formPubTypeData.day = formPubTypeData.day.padStart(2, '0')
  }
  if (formPubTypeData.dayaccessed ?? false) {
    formPubTypeData.dayaccessed = formPubTypeData.dayaccessed.padStart(2, '0')
  }
  return formPubTypeData
}

export const mapContributorCitationToBffFormat = (
  contributor: Contributor
): BffContributor => {
  const { firstName, middleName, lastName, data, function: fn } = contributor
  return {
    data: data ?? null,
    first: firstName ?? null,
    function: fn ?? null,
    middle: middleName ?? null,
    last: lastName ?? null,
  } as BffContributor
}

/**
 * @param state form state
 * @returns equivalent GQL citation
 */
export const formStateToCitationTransformation = (state: any): Citation => {
  const updatedState = { ...state }

  updatedState.source =
    state.source === SOURCE_WEBPAGE ? SourceType.WEBSITE : state.source
  updatedState.pubtype = state.pubtype ?? WEBSITE_PUBTYPE
  updatedState.annotation = state.annotation ?? ''
  updatedState.contributors = state.contributors ?? []

  if (updatedState && updatedState.pubtype) {
    Object.values(updatedState.pubtype).forEach((value: string) => {
      if (value !== 'PubType' && updatedState[value]) {
        const transformedPubonlineData = formatAutociteDate(updatedState[value])
        updatedState[value] = transformedPubonlineData
      }
    })
  }

  const { contributors, annotation, source, pubtype, ...rest } = updatedState

  const transformedContributor = contributors.map((contributor: BffContributor) =>
    mapContributorBffToCitationFormat(contributor)
  )
  /** citation data intialized with basic info */
  const citation: Citation = {
    ...rest,
    contributors: transformedContributor,
    annotation,
    data: {
      source,
      pubtype,
      annotation,
    },
  }
  /** adding pub info section data */
  const pubTypeMain = citation.data.pubtype.main
  const pubTypeSuffix = citation.data.pubtype.suffix
  if (pubTypeMain) {
    citation.data[pubTypeMain] = updatedState[pubTypeMain]
  }
  if (pubTypeSuffix) {
    citation.data[pubTypeSuffix] = updatedState[pubTypeSuffix]
  }
  /** collecting citing section data, if any */
  if (updatedState[updatedState.source]) {
    citation.data[updatedState.source] = updatedState[updatedState.source]
  }
  /** collecting DOI data, if present */
  if (updatedState.doi) {
    citation.data.doi = updatedState.doi
  }
  return citation
}

/**
 * @param citation incoming initialCitationState
 * @returns equivalent form state - to initialize form context
 */
export const citationToFormStateTransformation = (citation: IntialCitationData): any => {
  const citationContributor = citation.contributors ?? []
  /** basic setup for form state */
  const transformedContributor = citationContributor.map((contributor: Contributor) =>
    mapContributorCitationToBffFormat(contributor)
  )
  const formState: any = {
    source: citation.data.source,
    pubtype: citation.data.pubtype,
    contributors: transformedContributor,
    annotation: citation.annotation,
  }
  /** collecting pub info section data */
  const pubTypeMain = citation.data.pubtype?.main
  const pubTypeSuffix = citation.data.pubtype?.suffix
  if (pubTypeMain) {
    formState[pubTypeMain] = citation.data[pubTypeMain]
  }
  if (pubTypeSuffix) {
    formState[pubTypeSuffix] = citation.data[pubTypeSuffix]
  }
  /** collecting citing section data, if any */
  if (citation.data[citation.data.source]) {
    formState[citation.data.source] = citation.data[citation.data.source]
  }
  /** collecting DOI data, if present */
  if (citation.data.doi) {
    formState.doi = citation.data.doi
  }
  const missingRecommendedFields = validateMissingFormFields(
    formStateToCitationTransformation(formState)
  )
    ? ValidatorStates.MISSING
    : ValidatorStates.COMPLETE
  formState.validatorStatus = missingRecommendedFields
  return formState
}

export const getFormsBackButtonText = (
  formType: FormType,
  sourceType: string
): BackButtonTexts => {
  const skipEvalPage = !evalSourceTypes.includes(sourceType)

  switch (formType) {
    case 'manual':
      return BackButtonTexts.BACK_TO_SEARCH
    case 'new':
      return skipEvalPage
        ? BackButtonTexts.BACK_TO_RESULTS
        : BackButtonTexts.BACK_TO_REVIEW
    case 'edit':
      return BackButtonTexts.BACK_TO_CITATION_LIST
    default:
      return BackButtonTexts.CANCEL
  }
}
